import React, { useState, useEffect } from 'react';
import { 
  Table, Card, Button, Modal, Image, Tag, message, Space,
  Form, Input, Select, Row, Col, Checkbox, Typography
} from 'antd';
import { 
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, 
  FolderOpenOutlined, SearchOutlined, ReloadOutlined
} from '@ant-design/icons';
import api, { getImageUrl } from '../utils/api';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const HASH_BATCH_SIZE = 10;

/** 将数组按固定大小切分，例如 25 个 hash -> [[10],[10],[5]] */
const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

/** 按 image_hash 分批（每批最多 10 个）并行查库 */
const lookupMaterialsByHashes = async (imageHashes) => {
  const uniqueHashes = [...new Set(imageHashes.filter(Boolean))];
  if (uniqueHashes.length === 0) {
    return { hashMap: new Map(), batchCount: 0, hashCount: 0 };
  }

  const batches = chunkArray(uniqueHashes, HASH_BATCH_SIZE);
  console.log(
    `[素材匹配] 共 ${uniqueHashes.length} 个 image_hash，分 ${batches.length} 批查询（每批最多 ${HASH_BATCH_SIZE} 个）`
  );

  const responses = await Promise.all(
    batches.map((batch, index) => {
      console.log(`[素材匹配] 第 ${index + 1}/${batches.length} 批:`, batch);
      return api.post('/materials/lookup-by-hashes', { image_hashes: batch });
    })
  );

  const hashMap = new Map();
  for (const response of responses) {
    for (const material of response.data?.items || []) {
      if (material.facebook_hash) {
        hashMap.set(material.facebook_hash, material);
      }
    }
  }

  console.log(`[素材匹配] 数据库命中 ${hashMap.size} 个素材`);
  return { hashMap, batchCount: batches.length, hashCount: uniqueHashes.length };
};

const MaterialCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  
  // 创建素材集相关状态
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedMaterials, setSelectedMaterials] = useState([]);

  useEffect(() => {
    fetchCollections();
  }, []);
  // 搜索外部素材
  const handleSearchMaterials = async () => {
    try {
      const values = createForm.getFieldsValue();
      
      // 构建查询参数
      const params = {};
      if (values.roi) params.ROI = values.roi;
      if (values.orders) params.orders = values.orders;
      if (values.sevenDaysRoi) params.senvenRoi = values.sevenDaysRoi;
      params.limit = 100;
      
      console.log('搜索参数:', params);
      
      setSearchLoading(true);
      message.loading('正在搜索素材...', 0);
      
      // 构建完整URL并测试
      const queryString = new URLSearchParams(params).toString();
      const fullUrl = `https://www.readgz.com/index/index/sucaiList?${queryString}`;
      console.log('请求URL:', fullUrl);
      
      // 使用fetch API
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const resultData = await response.json();
      console.log('API返回结果:', resultData);
      
      message.destroy();
      
      // 获取外部素材数据 - 判断返回的数据结构
      let externalMaterials = [];
      if (Array.isArray(resultData)) {
        externalMaterials = resultData;
      } else if (resultData.data && Array.isArray(resultData.data)) {
        externalMaterials = resultData.data;
      } else if (resultData.list && Array.isArray(resultData.list)) {
        externalMaterials = resultData.list;
      }
      
      console.log('解析后的素材列表:', externalMaterials);

      // 1. 提取 readgz 返回的全部 image_hash
      const imageHashes = externalMaterials
        .map((item) => item.image_hash)
        .filter(Boolean);

      // 2. 超过 10 个会自动分批，多批并行请求 /materials/lookup-by-hashes
      const { hashMap: hashToMaterial, batchCount, hashCount } =
        await lookupMaterialsByHashes(imageHashes);

      // 3. 用查库结果与外部素材对齐
      const matchedResults = [];
      for (const external of externalMaterials) {
        const matched = hashToMaterial.get(external.image_hash);
        if (matched) {
          matchedResults.push({
            ...matched,
            externalData: external,
          });
        }
      }

      console.log(
        '匹配结果:',
        matchedResults,
        `(外部 ${externalMaterials.length} 条, hash ${hashCount} 个, ${batchCount} 批, 命中 ${matchedResults.length} 条)`
      );
      
      setSearchResults(matchedResults);
      if (matchedResults.length > 0) {
        message.success(
          `找到 ${matchedResults.length} 个匹配素材（${hashCount} 个 hash，分 ${batchCount} 批查询）`
        );
      } else {
        message.info(
          hashCount > 0
            ? `未找到匹配素材（已查询 ${hashCount} 个 hash，${batchCount} 批）`
            : '未找到匹配的素材'
        );
      }
      
    } catch (error) {
      console.error('Failed to search materials:', error);
      console.error('错误详情:', error.message, error.stack);
      message.destroy();
      message.error('搜索素材失败: ' + (error.message || '未知错误'));
    } finally {
      setSearchLoading(false);
    }
  };

  // 选择/取消选择素材
  const toggleMaterialSelection = (materialId) => {
    if (selectedMaterials.includes(materialId)) {
      setSelectedMaterials(selectedMaterials.filter(id => id !== materialId));
    } else {
      setSelectedMaterials([...selectedMaterials, materialId]);
    }
  };

  // 全选/取消全选
  const toggleAllSelection = (checked) => {
    if (checked) {
      setSelectedMaterials(searchResults.map(m => m.id));
    } else {
      setSelectedMaterials([]);
    }
  };

  // 创建素材集
  const handleCreateCollection = async () => {
    try {
      const values = createForm.getFieldsValue();
      
      if (!values.name) {
        message.error('请输入素材集名称');
        return;
      }
      
      if (selectedMaterials.length === 0) {
        message.error('请选择至少一个素材');
        return;
      }
      
      setSearchLoading(true);
      message.loading('正在创建素材集...', 0);
      
      await api.post('/materials/collections/', {
        name: values.name,
        description: values.description,
        material_ids: selectedMaterials
      });
      
      message.destroy();
      message.success('创建成功');
      
      // 关闭模态框并重置
      setCreateModalVisible(false);
      createForm.resetFields();
      setSearchResults([]);
      setSelectedMaterials([]);
      
      // 刷新列表
      fetchCollections(pagination.current, pagination.pageSize);
      
    } catch (error) {
      console.error('Failed to create collection:', error);
      message.destroy();
      message.error('创建失败');
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchCollections = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await api.get('/materials/collections/', {
        params: { page, page_size: pageSize },
      });
      const data = response.data || {};
      setCollections(data.items || []);
      setPagination({
        current: data.page || page,
        pageSize: data.page_size || pageSize,
        total: data.total || 0,
      });
    } catch (error) {
      console.error('Failed to fetch collections:', error);
      message.error('获取素材集列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (collectionId) => {
    try {
      const response = await api.get(`/materials/collections/${collectionId}`);
      setSelectedCollection(response.data);
      setDetailModalVisible(true);
    } catch (error) {
      console.error('Failed to fetch collection detail:', error);
      message.error('获取素材集详情失败');
    }
  };

  const handleDelete = async (collectionId) => {
    try {
      await api.delete(`/materials/collections/${collectionId}`);
      message.success('删除成功');
      fetchCollections(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Failed to delete collection:', error);
      message.error('删除失败');
    }
  };

  const columns = [
    {
      title: '素材集名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => handleViewDetail(record.id)}>
          <FolderOpenOutlined style={{ marginRight: 8 }} />
          {text}
        </span>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '素材数量',
      dataIndex: 'total_materials',
      key: 'total_materials',
      render: (text) => <span className="badge badge-info">{text}</span>,
    },
    {
      title: '使用次数',
      dataIndex: 'total_usage_count',
      key: 'total_usage_count',
      render: (text) => <span className="badge badge-success">{text || 0}</span>,
    },
    {
      title: '下次分配',
      dataIndex: 'last_material_index',
      key: 'last_material_index',
      render: (text, record) => {
        const total = record.total_materials || 0;
        if (total === 0) return '-';
        return <span>第 {text + 1} 个</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? '活跃' : '归档'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => handleViewDetail(record.id)}
          >
            查看
          </Button>
          <Button icon={<EditOutlined />} size="small">编辑</Button>
          <Button 
            danger 
            icon={<DeleteOutlined />} 
            size="small"
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card 
        title="素材集管理" 
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setCreateModalVisible(true);
            createForm.resetFields();
            setSearchResults([]);
            setSelectedMaterials([]);
          }}>
            创建素材集
          </Button>
        }
      >
        <Table
          dataSource={collections}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              fetchCollections(page, pageSize);
            },
          }}
        />
      </Card>

      <Modal
        title={`素材集: ${selectedCollection?.name}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={null}
      >
        {selectedCollection && (
          <div>
            <p><strong>描述:</strong> {selectedCollection.description || '无'}</p>
            <p><strong>素材数量:</strong> {selectedCollection.total_materials}</p>
            <p><strong>总使用次数:</strong> {selectedCollection.total_usage_count || 0} 次</p>
            <p><strong>下次分配起始位置:</strong> 第 {selectedCollection.last_material_index + 1} 个素材</p>
            <p><strong>创建时间:</strong> {new Date(selectedCollection.created_at).toLocaleString('zh-CN')}</p>
            
            <h3 style={{ marginTop: 24, marginBottom: 16 }}>素材列表</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
              {selectedCollection.materials?.map((material) => (
                <div key={material.id} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 8 }}>
                  <Image 
                    src={getImageUrl(material.file_url)} 
                    alt={material.name}
                    style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <p style={{ marginTop: 8, marginBottom: 4, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {material.name}
                  </p>
                  <Tag color={
                    material.source_type === 'uploaded' ? 'blue' : 
                    material.source_type === 'duplicate' ? 'orange' : 'gray'
                  }>
                    {material.source_type === 'uploaded' ? '上传' : 
                     material.source_type === 'duplicate' ? '重复' : '手动'}
                  </Tag>
                </div>
              ))}
            </div>
            
            {(!selectedCollection.materials || selectedCollection.materials.length === 0) && (
              <p style={{ textAlign: 'center', color: '#999', padding: 40 }}>
                该素材集暂无素材
              </p>
            )}
          </div>
        )}
      </Modal>
      
      {/* 创建素材集模态框 */}
      <Modal
        title="创建素材集"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
          setSearchResults([]);
          setSelectedMaterials([]);
        }}
        width={1200}
        footer={[
          <Button key="cancel" onClick={() => {
            setCreateModalVisible(false);
            createForm.resetFields();
            setSearchResults([]);
            setSelectedMaterials([]);
          }}>
            取消
          </Button>,
          <Button key="submit" type="primary" loading={searchLoading} onClick={handleCreateCollection}>
            确认创建
          </Button>
        ]}
      >
        <Form
          form={createForm}
          layout="vertical"
        >
          {/* 基本信息 */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="素材集名称"
                name="name"
                rules={[{ required: true, message: '请输入素材集名称' }]}
              >
                <Input placeholder="请输入素材集名称" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="描述" name="description">
                <TextArea placeholder="请输入描述（选填）" rows={1} />
              </Form.Item>
            </Col>
          </Row>
          
          {/* 筛选条件 */}
          <Card size="small" style={{ marginBottom: 16 }} title="搜索筛选条件">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="总ROI" name="roi">
                  <Input type="number" placeholder="总ROI筛选值" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="订单数" name="orders">
                  <Input type="number" placeholder="订单数筛选值" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="七日ROI" name="sevenDaysRoi">
                  <Input type="number" placeholder="七日ROI筛选值" />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="center">
              <Space>
                <Button type="primary" icon={<SearchOutlined />} loading={searchLoading} onClick={handleSearchMaterials}>
                  搜索
                </Button>
                <Button icon={<ReloadOutlined />} onClick={() => {
                  createForm.resetFields(['roi', 'orders', 'sevenDaysRoi']);
                  setSearchResults([]);
                  setSelectedMaterials([]);
                }}>
                  重置
                </Button>
              </Space>
            </Row>
          </Card>
          
          {/* 搜索结果列表 */}
          {searchResults.length > 0 && (
            <Card size="small" title={`搜索结果 (已选择: ${selectedMaterials.length} 个)`}>
              <div style={{ marginBottom: 12 }}>
                <Checkbox
                  checked={selectedMaterials.length === searchResults.length && searchResults.length > 0}
                  indeterminate={selectedMaterials.length > 0 && selectedMaterials.length < searchResults.length}
                  onChange={(e) => toggleAllSelection(e.target.checked)}
                >
                  全选
                </Checkbox>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, maxHeight: 400, overflowY: 'auto' }}>
                {searchResults.map((material) => (
                  <div key={material.id} style={{
                    border: selectedMaterials.includes(material.id) ? '2px solid #1890ff' : '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: 8,
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                  onClick={() => toggleMaterialSelection(material.id)}
                  >
                    {selectedMaterials.includes(material.id) && (
                      <div style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        background: '#1890ff',
                        color: 'white',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12
                      }}>
                        ✓
                      </div>
                    )}
                    <Image 
                      src={getImageUrl(material.file_url)} 
                      alt={material.name}
                      style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4 }}
                    />
                    <p style={{ marginTop: 8, marginBottom: 4, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {material.name}
                    </p>
                    {/* 显示外部数据指标 */}
                    {material.externalData && (
                      <div style={{ fontSize: 11, color: '#666' }}>
                        <div>
                          <Text type="secondary">ROI:</Text> {material.externalData.ROI} | 
                          <Text type="secondary"> 订单:</Text> {material.externalData.purchase_counts}
                        </div>
                        <div>
                          <Text type="secondary">消耗:</Text> {material.externalData.spends} | 
                          <Text type="secondary"> 7日ROI:</Text> {material.externalData.seven_days_roi}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default MaterialCollections;
